import { Box, useTheme } from '@mui/material';
import { useField } from 'formik';
import { type FC, useCallback, useMemo, useState } from 'react';

import { getTenantUnits } from '@core/configuration';
import type { Address } from '@core/types/api/Address/Address';
import type { LocationResult } from '@core/types/api/LocationResult/LocationResult';
import { hasValue } from '@core/utils/equality';
import { mapViewFor, resolveAddressCountry } from '@core/utils/subdivisions';
import type { ExcludeMethodsDeep } from '@core/utils/types';
import { ABOVE_CLUSTERS_Z_INDEX, PIN_VISIBILITY_STORAGE_KEY } from '@web/constants/map';

import { useSlabQueryFetcher } from '../../hooks/useSlabQuery';
import type { QueryRouteBarrelTypes } from '../../utils/ApiClient';
import type { QueryError } from '../../utils/Query';
import { CompetitorPlantMarkers } from '../MapPins/CompetitorPlantMarkers';
import { JobSiteMarkers } from '../MapPins/JobSiteMarkers';
import { MapFilters } from '../MapPins/MapFilters';
import type { PinType } from '../MapPins/mapPins';
import { createPinClusterRenderer } from '../MapPins/pinClusterRenderer';
import { PlantMarkers } from '../MapPins/PlantMarkers';
import { usePinVisibility } from '../MapPins/usePinVisibility';
import { MAP_ZOOM_CLOSE, SlabMap } from '../SlabMap/SlabMap';
import { SlabMarker } from '../SlabMap/SlabMarker';
import { SlabMarkerCluster } from '../SlabMap/SlabMarkerCluster';
import { useAddress } from './hooks/useAddress';

type LocationPosition = {
  lat: number;
  lng: number;
};

type Props = {
  name: string;
  onAddressChange?(address: ExcludeMethodsDeep<Address>): void;
  pinTypes?: PinType[];
};

export const MapField: FC<Props> = ({ name, onAddressChange, pinTypes = [] }) => {
  const [field] = useField<Address>(name);
  const [countryField] = useField<string | null>(`${name}.country`);
  const theme = useTheme();
  const clusterRenderer = useMemo(() => createPinClusterRenderer(theme), [theme]);
  const { pinVisibility, onTogglePinVisibility } = usePinVisibility(
    PIN_VISIBILITY_STORAGE_KEY.addressMap,
  );

  const { handleAddressChange } = useAddress({ name });

  const { value: fieldAddress } = field;
  const { latitude, longitude } = fieldAddress;

  const parsedLat = parseFloat(hasValue(latitude) ? latitude : '');
  const parsedLng = parseFloat(hasValue(longitude) ? longitude : '');

  const isValidLocation = !Number.isNaN(parsedLng) && !Number.isNaN(parsedLat);

  const location: google.maps.LatLngLiteral | undefined = useMemo(
    () => (isValidLocation ? { lat: parsedLat, lng: parsedLng } : undefined),
    [isValidLocation, latitude, longitude],
  );

  // Nothing geocoded yet, so follow the selected country instead of wherever
  // the map happened to be centered for the previous one.
  const country = resolveAddressCountry(countryField.value, getTenantUnits().locale);
  const countryView = mapViewFor(country);

  const [clickZoom, setClickZoom] = useState<number | null>(null);
  const zoom = clickZoom ?? (isValidLocation ? MAP_ZOOM_CLOSE : countryView.zoom);

  const mapsConversionFetcher = useSlabQueryFetcher<
    'GET maps conversion',
    QueryRouteBarrelTypes['GET maps conversion'],
    QueryError
  >();

  const handleChange = (newLocation: Partial<LocationResult>): void => {
    const address = handleAddressChange(newLocation);

    onAddressChange?.(address);
  };

  const locationRequest = useCallback(
    async ({ lat, lng }: LocationPosition): Promise<LocationResult | null> => {
      try {
        const mapsConversionQuery = await mapsConversionFetcher('GET maps conversion', {
          queryParams: { address: '', lat: String(lat), long: String(lng) },
        });

        return mapsConversionQuery;
      } catch {
        return null;
      }
    },
    [],
  );

  const handleMapClick = async (event: google.maps.MapMouseEvent): Promise<void> => {
    if (!hasValue(event.latLng)) return;

    const newLocation = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    locationRequest(newLocation).then((locationResponse) => {
      if (hasValue(locationResponse)) {
        handleChange({ ...locationResponse, location: newLocation });
      }
    });

    setClickZoom(MAP_ZOOM_CLOSE);
  };

  const hasPinFilters = pinTypes.length > 0;
  const showsPinType = (type: PinType): boolean => pinTypes.includes(type) && pinVisibility[type];

  return (
    <Box position='relative'>
      {hasPinFilters && (
        <MapFilters
          pinTypes={pinTypes}
          pinVisibility={pinVisibility}
          onTogglePinVisibility={onTogglePinVisibility}
        />
      )}
      <SlabMap
        center={location ?? countryView.center}
        zoom={zoom}
        onClick={handleMapClick}
        gestureHandling='cooperative'
      >
        <SlabMarker location={location} zIndex={ABOVE_CLUSTERS_Z_INDEX} />
        {hasPinFilters && (
          <SlabMarkerCluster renderer={clusterRenderer}>
            {showsPinType('competitor-plant') && <CompetitorPlantMarkers onClickPin={() => {}} />}
            {showsPinType('plant') && <PlantMarkers onClickPin={() => {}} />}
            {showsPinType('job-site') && <JobSiteMarkers onClickPin={() => {}} />}
          </SlabMarkerCluster>
        )}
      </SlabMap>
    </Box>
  );
};
